import { getTextAnnotationBox, useImageAnnotatorContext } from "@msuite/image-annotator";
import type { TextAnnotation } from "@msuite/utils/types";
import { useEffect, useRef } from "react";
import { useTranslation } from "../../localization";

/**
 * The caret for a text annotation, laid over the SVG panel it belongs to.
 *
 * It is a plain textarea in normal DOM rather than a `foreignObject`, because form controls inside
 * SVG have a long history of focus and caret bugs in WebKit — which the Tauri desktop build runs
 * on. Sitting outside the `<svg>` costs one conversion: image units become container pixels by
 * multiplying with `baseScale`.
 */
export function TextAnnotationEditor() {
	/** Context */
	const context = useImageAnnotatorContext();

	/** Hooks */
	const { t } = useTranslation();

	/** Refs */
	const inputRef = useRef<HTMLTextAreaElement>(null);

	/** Computed */
	const editId = context.controller.textEditId;
	const annotation = editId
		? (context.controller.annotations.find((a) => a.id === editId) ?? null)
		: null;
	const target = annotation?.type === "text" ? (annotation as TextAnnotation) : null;

	/** Effects */
	useEffect(() => {
		const input = inputRef.current;
		if (!input) return;
		input.focus();
		/** Start at the end of the existing label rather than replacing it. */
		input.setSelectionRange(input.value.length, input.value.length);
	}, [editId]);

	/** Functions */
	function handleOnKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
		if (e.key !== "Escape") return;
		e.stopPropagation();
		context.controller.handleOnEndTextEdit();
	}

	/** Guard */
	if (context.controller.isReadOnly) return null;
	if (!target) return null;

	/** Render */
	const box = getTextAnnotationBox(
		target,
		context.baseScale,
		t("image_annotator.text_placeholder"),
	);
	const scale = context.baseScale;

	return (
		<textarea
			ref={inputRef}
			value={target.text}
			placeholder={t("image_annotator.text_placeholder")}
			spellCheck={false}
			/** The SVG text only breaks on newlines, so soft wrapping here would misplace lines. */
			wrap="off"
			className="absolute m-0 resize-none overflow-hidden border-none bg-transparent outline-none"
			style={{
				left: box.x * scale,
				top: box.y * scale,
				width: box.width * scale,
				height: box.height * scale,
				padding: `${box.paddingY * scale}px ${box.paddingX * scale}px`,
				fontFamily: "Arial",
				fontSize: box.fontSize * scale,
				lineHeight: `${box.lineHeight * scale}px`,
				color: target.strokeColor || "#fff",
				caretColor: target.strokeColor || "#fff",
			}}
			onChange={(e) => context.controller.handleOnUpdateSelection({ text: e.target.value })}
			onKeyDown={handleOnKeyDown}
			onBlur={() => context.controller.handleOnEndTextEdit()}
			onClick={(e) => e.stopPropagation()}
		/>
	);
}
